/** Status do funil Onboarding (Firestore: slugs estáveis). */
export const ONBOARDING_STATUS = {
  NOVO: 'novo',
  EM_VALIDACAO: 'em_validacao',
  APROVADO: 'aprovado',
  PENDENTE: 'pendente',
  CONVERTIDO: 'convertido',
  RECUSADO: 'recusado',
};

export const ONBOARDING_STATUS_ORDER = [
  ONBOARDING_STATUS.NOVO,
  ONBOARDING_STATUS.EM_VALIDACAO,
  ONBOARDING_STATUS.APROVADO,
  ONBOARDING_STATUS.PENDENTE,
  ONBOARDING_STATUS.CONVERTIDO,
  ONBOARDING_STATUS.RECUSADO,
];

export const ONBOARDING_STATUS_LABEL = {
  [ONBOARDING_STATUS.NOVO]: 'Novo',
  [ONBOARDING_STATUS.EM_VALIDACAO]: 'Em validação',
  [ONBOARDING_STATUS.APROVADO]: 'Aprovado',
  [ONBOARDING_STATUS.PENDENTE]: 'Pendente',
  [ONBOARDING_STATUS.CONVERTIDO]: 'Convertido',
  [ONBOARDING_STATUS.RECUSADO]: 'Recusado',
};

/**
 * Fase 2 — **Nascimento** do onboarding (imutável na prática; não sobrescrever no submit do site).
 * Preferir este campo para “como nasceu”. Legado: inferido de `onboardingCreationMode` / `crmEntrySource`.
 */
export const ONBOARDING_ORIGIN_BIRTH = {
  MANUAL: 'manual',
  FROM_LEAD: 'from_lead',
  DASHBOARD_CLIENT_INVITE: 'dashboard_client_invite',
  SITE_ONBOARDING: 'site_onboarding',
  SITE_INVITE_SUBMIT: 'site_invite_submit',
};

/**
 * Fase 2 — **Última origem de preenchimento** dos dados (mutável).
 * Legado: inferido de `crmEntrySource` + `dashboardSelfFillPending`.
 */
export const LAST_FILL_SOURCE = {
  DASHBOARD_CREATE: 'dashboard_create',
  DASHBOARD_EDIT: 'dashboard_edit',
  WEBSITE_ONBOARDING_FORM: 'website_onboarding_form',
  WEBSITE_INVITE_SUBMIT: 'website_invite_submit',
};

/** Fase operacional do auto-preenchimento (convite dashboard) — derivado; legado usa `dashboardSelfFillPending` + timestamps. */
export const CLIENT_SELF_FILL_PHASE = {
  NA: 'na',
  PENDING: 'pending',
  LINK_SENT: 'link_sent',
  OPENED: 'opened',
  COMPLETED: 'completed',
};

/** Como o onboarding entrou no CRM (Firestore: `crmEntrySource`). Mantido por compatibilidade; semântica mistura nascimento + último submit. */
export const ONBOARDING_ENTRY_SOURCE = {
  FROM_LEAD: 'from_lead',
  DIRECT: 'direct',
  /** Reservado — convite público / token (próxima etapa). */
  INVITE: 'invite',
};

/** @type {readonly string[]} */
export const ONBOARDING_ENTRY_SOURCE_VALUES = Object.freeze([
  ONBOARDING_ENTRY_SOURCE.FROM_LEAD,
  ONBOARDING_ENTRY_SOURCE.DIRECT,
  ONBOARDING_ENTRY_SOURCE.INVITE,
]);

/**
 * @param {unknown} raw
 * @returns {string} slug conhecido ou `''` (legado / inválido)
 */
export function normalizeOnboardingCrmEntrySource(raw) {
  const s = String(raw ?? '').trim();
  return ONBOARDING_ENTRY_SOURCE_VALUES.includes(s) ? s : '';
}

/**
 * Valor canónico de nascimento (campo novo ou inferência legada).
 * @param {Record<string, unknown>|null|undefined} doc — normalizado (`normalizeOnboardingDoc`) ou raw
 */
export function resolveOnboardingOriginBirth(doc) {
  const c = String(doc?.onboardingOriginBirth ?? '').trim();
  if (c) return c;
  if (doc?.onboardingCreationMode === 'client_invite') return ONBOARDING_ORIGIN_BIRTH.DASHBOARD_CLIENT_INVITE;
  const src = normalizeOnboardingCrmEntrySource(doc?.crmEntrySource);
  if (src === ONBOARDING_ENTRY_SOURCE.INVITE) return ONBOARDING_ORIGIN_BIRTH.SITE_INVITE_SUBMIT;
  if (src === ONBOARDING_ENTRY_SOURCE.FROM_LEAD) return ONBOARDING_ORIGIN_BIRTH.FROM_LEAD;
  if (src === ONBOARDING_ENTRY_SOURCE.DIRECT) return ONBOARDING_ORIGIN_BIRTH.MANUAL;
  if (String(doc?.leadId ?? '').trim()) return 'legacy_with_lead';
  return '';
}

/**
 * Última origem de preenchimento (campo novo ou inferência legada).
 * @param {Record<string, unknown>|null|undefined} doc
 */
export function resolveLastFillSource(doc) {
  const c = String(doc?.lastFillSource ?? '').trim();
  if (c) return c;
  const src = normalizeOnboardingCrmEntrySource(doc?.crmEntrySource);
  if (src === ONBOARDING_ENTRY_SOURCE.INVITE) return LAST_FILL_SOURCE.WEBSITE_INVITE_SUBMIT;
  if (doc?.dashboardSelfFillPending === true) return LAST_FILL_SOURCE.DASHBOARD_CREATE;
  if (src === ONBOARDING_ENTRY_SOURCE.FROM_LEAD || src === ONBOARDING_ENTRY_SOURCE.DIRECT) {
    return LAST_FILL_SOURCE.DASHBOARD_CREATE;
  }
  return '';
}

/**
 * Estado operacional do fluxo “cliente preenche” (convite criado no dashboard).
 * @param {Record<string, unknown>|null|undefined} doc
 */
export function resolveClientSelfFillPhase(doc) {
  const isInviteMode = doc?.onboardingCreationMode === 'client_invite';
  if (!isInviteMode && doc?.dashboardSelfFillPending !== true) return CLIENT_SELF_FILL_PHASE.NA;
  if (doc?.dashboardSelfFillPending === true) {
    const opened = doc.clientInviteLinkOpenedAt != null && Number.isFinite(doc.clientInviteLinkOpenedAt);
    if (opened) return CLIENT_SELF_FILL_PHASE.OPENED;
    const sent = doc.clientInviteLastSentAt != null && Number.isFinite(doc.clientInviteLastSentAt);
    if (sent) return CLIENT_SELF_FILL_PHASE.LINK_SENT;
    return CLIENT_SELF_FILL_PHASE.PENDING;
  }
  if (isInviteMode) return CLIENT_SELF_FILL_PHASE.COMPLETED;
  return CLIENT_SELF_FILL_PHASE.NA;
}

/** Rótulo curto só do **nascimento** (dimensão A). */
export function getOnboardingOriginBirthLabel(doc) {
  const b = resolveOnboardingOriginBirth(doc);
  switch (b) {
    case ONBOARDING_ORIGIN_BIRTH.MANUAL:
      return 'Criação manual (dashboard)';
    case ONBOARDING_ORIGIN_BIRTH.FROM_LEAD:
      return 'A partir de lead (dashboard)';
    case ONBOARDING_ORIGIN_BIRTH.DASHBOARD_CLIENT_INVITE:
      return 'Convite ao cliente (dashboard)';
    case ONBOARDING_ORIGIN_BIRTH.SITE_ONBOARDING:
      return 'Formulário do site';
    case ONBOARDING_ORIGIN_BIRTH.SITE_INVITE_SUBMIT:
      return 'Criado via convite (site)';
    case 'legacy_with_lead':
      return 'Legado — com lead';
    default:
      return 'Legado — proveniência não registada';
  }
}

/**
 * Estado resumido na lista — dimensão C (auto-preenchimento); legado: `dashboardSelfFillPending` + timestamps.
 * @param {Record<string, unknown>|null|undefined} doc
 */
export function getOnboardingSelfFillInviteHint(doc) {
  const ph = resolveClientSelfFillPhase(doc);
  if (ph === CLIENT_SELF_FILL_PHASE.OPENED) return 'Convite aberto · aguardando preenchimento';
  if (ph === CLIENT_SELF_FILL_PHASE.LINK_SENT) return 'Convite enviado · não aberto';
  if (ph === CLIENT_SELF_FILL_PHASE.PENDING) return 'A gerar convite…';
  return '';
}

/**
 * Rótulo composto para UI e CSV: **nascimento** + sufixo quando o **último preenchimento** foi no site.
 * @param {Record<string, unknown>|null|undefined} doc
 */
export function getOnboardingEntrySourceLabel(doc) {
  const birth = getOnboardingOriginBirthLabel(doc);
  const birthKey = resolveOnboardingOriginBirth(doc);
  const last = resolveLastFillSource(doc);
  if (
    last === LAST_FILL_SOURCE.WEBSITE_INVITE_SUBMIT &&
    birthKey !== ONBOARDING_ORIGIN_BIRTH.SITE_INVITE_SUBMIT &&
    birthKey !== ''
  ) {
    return `${birth} · dados completados no site`;
  }
  if (last === LAST_FILL_SOURCE.WEBSITE_ONBOARDING_FORM && birthKey === ONBOARDING_ORIGIN_BIRTH.SITE_ONBOARDING) {
    return birth;
  }
  return birth;
}
